import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Download, Filter, RefreshCw, ChevronDown, ChevronUp } from 'lucide-react';
import { api } from '../services/api';
import type { LogType, LogLevel } from '../types';
import { format } from 'date-fns';

export default function Logs() {
  const [page, setPage] = useState(1);
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [levelFilter, setLevelFilter] = useState<string>('');
  const [projectFilter] = useState<string>('');
  const [expandedLog, setExpandedLog] = useState<string | null>(null);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['logs', page, typeFilter, levelFilter, projectFilter],
    queryFn: () =>
      api.logs.list({
        page,
        limit: 50,
        type: typeFilter || undefined,
        level: levelFilter || undefined,
        projectId: projectFilter || undefined,
      }),
  });

  const { data: stats } = useQuery({
    queryKey: ['log-stats'],
    queryFn: api.logs.stats,
  });

  const typeOptions: { value: string; label: string }[] = [
    { value: '', label: 'Alle Typen' },
    { value: 'BACKUP', label: 'Backup' },
    { value: 'HEALTH', label: 'Health' },
    { value: 'VALIDATOR', label: 'Validator' },
    { value: 'SYSTEM', label: 'System' },
    { value: 'NOTIFICATION', label: 'Benachrichtigung' },
  ];

  const levelOptions: { value: string; label: string }[] = [
    { value: '', label: 'Alle Level' },
    { value: 'DEBUG', label: 'Debug' },
    { value: 'INFO', label: 'Info' },
    { value: 'WARNING', label: 'Warnung' },
    { value: 'ERROR', label: 'Fehler' },
  ];

  const getLevelColor = (level: LogLevel) => {
    switch (level) {
      case 'ERROR':
        return 'bg-red-100 text-red-700';
      case 'WARNING':
        return 'bg-yellow-100 text-yellow-700';
      case 'INFO':
        return 'bg-blue-100 text-blue-700';
      case 'DEBUG':
        return 'bg-gray-100 text-gray-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const getTypeIcon = (type: LogType) => {
    switch (type) {
      case 'BACKUP':
        return 'B';
      case 'HEALTH':
        return 'H';
      case 'VALIDATOR':
        return 'V';
      case 'SYSTEM':
        return 'S';
      case 'NOTIFICATION':
        return 'N';
      default:
        return '?';
    }
  };

  const handleExport = () => {
    const url = api.logs.getExportUrl({
      type: typeFilter || undefined,
      level: levelFilter || undefined,
      projectId: projectFilter || undefined,
    });
    window.open(url, '_blank');
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Logs</h1>
        <div className="flex gap-2">
          <button onClick={() => refetch()} className="btn btn-secondary flex items-center gap-2">
            <RefreshCw className="w-4 h-4" />
            Aktualisieren
          </button>
          <button onClick={handleExport} className="btn btn-primary flex items-center gap-2">
            <Download className="w-4 h-4" />
            CSV Export
          </button>
        </div>
      </div>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="card p-4">
            <p className="text-sm text-gray-500">Gesamt</p>
            <p className="text-2xl font-bold">{stats.total.toLocaleString()}</p>
          </div>
          <div className="card p-4">
            <p className="text-sm text-gray-500">Letzte 24h</p>
            <p className="text-2xl font-bold">{stats.last24h.toLocaleString()}</p>
          </div>
          <div className="card p-4">
            <p className="text-sm text-gray-500">Fehler (24h)</p>
            <p className="text-2xl font-bold text-red-600">
              {stats.byLevel?.ERROR || 0}
            </p>
          </div>
          <div className="card p-4">
            <p className="text-sm text-gray-500">Warnungen (24h)</p>
            <p className="text-2xl font-bold text-yellow-600">
              {stats.byLevel?.WARNING || 0}
            </p>
          </div>
        </div>
      )}

      <div className="card p-4 mb-6">
        <div className="flex flex-wrap gap-4">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-gray-400" />
            <select
              value={typeFilter}
              onChange={(e) => {
                setTypeFilter(e.target.value);
                setPage(1);
              }}
              className="input w-auto"
            >
              {typeOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          <select
            value={levelFilter}
            onChange={(e) => {
              setLevelFilter(e.target.value);
              setPage(1);
            }}
            className="input w-auto"
          >
            {levelOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        </div>
      ) : (
        <>
          <div className="card overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">
                    Zeit
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">
                    Level
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">
                    Typ
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">
                    Projekt
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">
                    Nachricht
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {data?.logs.map((log) => {
                  const isExpanded = expandedLog === log.id;
                  const isLongMessage = log.message.length > 80;

                  return (
                    <tr
                      key={log.id}
                      className={`hover:bg-gray-50 ${isLongMessage ? 'cursor-pointer' : ''}`}
                      onClick={() => isLongMessage && setExpandedLog(isExpanded ? null : log.id)}
                    >
                      <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap align-top">
                        {format(new Date(log.createdAt), 'dd.MM.yyyy HH:mm:ss')}
                      </td>
                      <td className="px-4 py-3 align-top">
                        <span
                          className={`inline-block px-2 py-1 text-xs font-medium rounded ${getLevelColor(
                            log.level
                          )}`}
                        >
                          {log.level}
                        </span>
                      </td>
                      <td className="px-4 py-3 align-top">
                        <span className="inline-flex items-center justify-center w-6 h-6 bg-gray-200 rounded text-xs font-medium">
                          {getTypeIcon(log.type)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm align-top">
                        {log.project?.name || '-'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700 align-top">
                        <div className="flex items-start gap-2">
                          <div className={isExpanded ? '' : 'max-w-md'}>
                            {isExpanded ? (
                              <div className="whitespace-pre-wrap break-all font-mono text-xs bg-gray-50 p-2 rounded">
                                {log.message}
                              </div>
                            ) : (
                              <span className={isLongMessage ? 'line-clamp-1' : ''}>
                                {log.message}
                              </span>
                            )}
                          </div>
                          {isLongMessage && (
                            <button
                              className="flex-shrink-0 text-gray-400 hover:text-gray-600"
                              onClick={(e) => {
                                e.stopPropagation();
                                setExpandedLog(isExpanded ? null : log.id);
                              }}
                            >
                              {isExpanded ? (
                                <ChevronUp className="w-4 h-4" />
                              ) : (
                                <ChevronDown className="w-4 h-4" />
                              )}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {data && data.pagination.totalPages > 1 && (
            <div className="flex items-center justify-between mt-4">
              <p className="text-sm text-gray-600">
                Zeige {(page - 1) * 50 + 1} bis{' '}
                {Math.min(page * 50, data.pagination.total)} von{' '}
                {data.pagination.total} Einträgen
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="btn btn-secondary disabled:opacity-50"
                >
                  Zurück
                </button>
                <button
                  onClick={() => setPage((p) => p + 1)}
                  disabled={page >= data.pagination.totalPages}
                  className="btn btn-secondary disabled:opacity-50"
                >
                  Weiter
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
